import merge from 'lodash.merge';

export class AbstractControl {
    constructor(
        node,
        options = {
            validators: [],
            validateOnBlur: false,
        }
    ) {
        if (!node) {
            console.error(`node parameter is required in ${this.constructor.name}, but now node is`, node);
            return;
        }
        this.node = node;
        this._validators = options.validators;
        this._validateOnBlur = options.validateOnBlur;
        this._errors = {};
    }

    query = {};
    states = {};

    init() {
        if (document.readyState !== 'complete') {
            window.addEventListener('load', () => {
                this.init();
            });

            return;
        }

        this.checkDirty();

        this.control.addEventListener('focus', () => {
            this.onFocus();
        });

        this.control.addEventListener('blur', () => {
            this.onBlur();

            if (this._validateOnBlur === true) {
                this.validate();
            }
        });

        this.control.addEventListener('input', () => {
            this.onInput();
        });
    }

    onFocus() {
        this.node.classList.add(this.states.focused);
    }

    onBlur() {
        this.node.classList.remove(this.states.focused);

        this.markAsTouched();
    }

    onInput() {
        this.node.classList.add(this.states.touched);

        this.checkDirty();
    }

    get control() {
        const node = this.node.querySelector(this.query.control);

        if (!('control' in this.query)) {
            console.error(
                `AbstractControl should be extend by ${this.constructor.name} class with 'query.control' property`
            );
        }

        if (node === null) {
            console.error(
                `Can't get control of ${this.constructor.name}. In your markup should be HTMLElement with class ${this.query.control}, root element:`,
                this.node
            );
        }

        return node;
    }

    get status() {
        if (this.disabled === true) {
            return 'DISABLED';
        } else if (this.invalid === true) {
            return 'INVALID';
        }

        return 'VALID';
    }

    get value() {
        return this.control.value;
    }

    set value(value) {
        this.control.value = value;
    }

    disable() {
        this.node.classList.add(this.states.disabled);
        this.control.disabled = true;
    }

    enable() {
        this.node.classList.remove(this.states.disabled);
        this.control.disabled = false;
    }

    checkDirty() {
        if (this.value.length > 0) {
            this.markAsDirty();
        } else {
            this.markAsPristine();
        }
    }

    markAsDirty() {
        this.node.classList.add(this.states.dirty);
    }

    markAsPristine() {
        this.node.classList.remove(this.states.dirty);
    }

    markAsInvalid() {
        this.node.classList.remove(this.states.valid);
        this.node.classList.add(this.states.invalid);
    }

    markAsValid() {
        this.node.classList.remove(this.states.invalid);
        this.node.classList.add(this.states.valid);
    }

    markAsTouched() {
        this.node.classList.add(this.states.touched);
    }

    markAsUntouched() {
        this.node.classList.remove(this.states.touched);
    }

    get invalid() {
        return this.node.classList.contains(this.states.invalid);
    }

    get valid() {
        return !this.invalid;
    }

    get disabled() {
        return this.node.classList.contains(this.states.disabled) && this.control.disabled;
    }

    get enabled() {
        return !this.disabled;
    }

    get dirty() {
        return this.node.classList.contains(this.states.dirty);
    }

    get focused() {
        return this.node.classList.contains(this.states.focused);
    }

    get unfocused() {
        return !this.focused;
    }

    validate() {
        this._errors = {};
        this._validators.forEach((validator) => {
            const validatorResult = validator(this.value);

            if (validatorResult !== null) {
                merge(this._errors, validatorResult);
            }
        });

        if (Object.keys(this._errors).length > 0) {
            this.markAsInvalid();
        } else {
            this.markAsValid();
        }
    }

    get errors() {
        return this._errors;
    }

    addValidator(validator) {
        this._validators.push(validator);
    }
}



// WEBPACK FOOTER //
// ./node_modules/wg5/src/modules/atoms/abstract-control/AbstractControl.js